import Crew from "../crew/Crew";
import { TerrainGenerator } from "../generator/TerrainGenerator";
import PerformanceMetrics from "../performance/PerformanceMetrics";
import GridSaveManager from "../save/GridSaveManager";
import Grid from "./Grid";
import GridInversionOfControl from "./GridInversionOfControl";
import GridTile from "./GridTile";
import { SpecialTileTypes } from "./TileTypes";
import VectorField from "./VectorField";
import WorldGrid from "./WorldGrid";

export default class GridLoader {
    /**
     * @param {GridSaveManager} gridSaveManager
     * @param {TerrainGenerator} terrainGenerator
     * @param {WorldGrid} worldGrid
     * @param {Crew} crew
     * @param {VectorField} vectorField
     */
    constructor(gridSaveManager, terrainGenerator, worldGrid, crew, vectorField) {
        /**
         * @constant
         * @type {GridSaveManager}
         */
        this.gridSaveManager = gridSaveManager;
        /**
         * @constant
         * @type {TerrainGenerator}
         */
        this.terrainGenerator = terrainGenerator;
        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;
        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;
    }

    /**
     * @param {Grid} grid 
     * @param {number} x The new grid origin x-coordinate.
     * @param {number} y The new grid origin y-coordinate.
     */
    loadGrid(grid, x, y) {
        // Sanity check.
        if (grid.isLoaded()) {
            this.unloadGrid(grid);
        }

        // Update origin
        grid.origin.set(x, y);

        // Initialize all the grid zones/regions/tiles for the new grid origin.
        grid.initializeZonesForGrid(); // <-- updates the origins of all zones/regions/tiles.

        this.loadOrGenerateTerrain(grid, false);
    }

    // /**
    //  * Note: This is not used in the game. We only used this in some test code when figuring out terrain generation.
    //  * @param {Grid} grid 
    //  */
    // regenerateGrid(grid) {

    //     if (grid.isLoaded()) {
    //         this.unloadGridInternal(grid, false);
    //     }

    //     grid.initializeZonesForGrid();

    //     this.loadOrGenerateTerrain(grid, true);
    // }

    /**
     * @param {Grid} grid 
     */
    unloadGrid(grid) {
        this.unloadGridInternal(grid, true);
    }

    /**
     * @private
     * @param {Grid} grid
     * @param {boolean} saveGridState
     */
    unloadGridInternal(grid, saveGridState) {

        // Save the grid state before we unload it.
        // Don't bother saving the grid state if tile health is negligible.
        if (saveGridState && grid.loaded && !this.vectorField.tileHealthNegligible) {
            this.gridSaveManager.saveGridState(grid);
        }

        // Handle case where the character is in the grid that is being unloaded.
        const miners = this.crew.miners;
        for (let i = 0; i < miners.length; i++) {
            const character = miners[i];
            if (character.position.tile && character.position.tile.getGrid() === grid) {
                console.log('GridLoader.unloadGridInternal() Party character is in grid that is being unloaded.');
                character.onInvalidGrid();
            }
        }

        grid.unloadGrid(); // This will unload any character in the grid

        // Let the vector field know that the grid has been unloaded.
        this.vectorField.onGridUnload(grid);

        // 
        this.crew.onGridUnload(grid);

    }

    /**
     * @private
     * @param {Grid} grid 
     * @param {boolean} forceGeneration
     */
    loadOrGenerateTerrain(grid, forceGeneration) {
        // If we cannot load the grid from the save, then we will need to generate it.
        const loadGridResult = !forceGeneration && this.gridSaveManager.loadGridState(grid);

        if (!loadGridResult) {
            PerformanceMetrics.startTime('GenerateTerrain');

            // Clear the edge/border neighbors because lots of them are referencing the
            // neighbor tiles from grids that they are no longer neighbors with. 
            grid.nullifyBorderNeighbors();

            this.terrainGenerator.generateTerrain(grid);
            PerformanceMetrics.endTime('GenerateTerrain');

            // Flag the state change as true.
            grid.markGridAsChanged();
        }
        else {
            // Even if we successfully load the grid from the save, we still need to generate the
            // background sprites for the tiles. We don't bother putting that stuff in the save file.
            this.terrainGenerator.generateTileBackgrounds(grid);
        }
    }

    /**
     * @param {Grid} grid 
     */
    onTerrainGenerationComplete(grid) {

        // The grid can be marked as changed while it is being loaded. Let's reset that at this point.
        grid.gridChangeCount = 0;

        // Mark the grid as loaded.
        grid.loaded = true;

        //////////////////////////////
        // Sky sunlight
        //////////////////////////////

        GridInversionOfControl.allTilesInGrid(grid, 
            /**
             * @param {GridTile} tile 
             */
            function(tile) {
                if (tile.open && tile.tileType === SpecialTileTypes.SKY && !tile.lighting.sunlightFull) {
                    tile.handleSunlightStart();
                }
            }
        );
    }

}